/**
 * VYV Mirror — Presence Engine Configuration
 *
 * Defines visual parameters for each avatar state.
 * All values are designed for smooth CSS transitions.
 * This config is the single source of truth for Mirror atmosphere.
 */

export type MirrorPresenceState =
  | "idle"
  | "listening"
  | "thinking"
  | "speaking";

export interface PresenceVisuals {
  /** CSS filter applied to the avatar video/image */
  avatarFilter: string;
  /** Opacity of the avatar element (0–1) */
  avatarOpacity: number;
  /** Scale multiplier for the avatar container */
  avatarScale: number;

  /** Primary halo radial-gradient opacity multiplier */
  haloIntensity: number;
  /** Halo spread size in px */
  haloSize: number;
  /** Halo hue (CSS hsl hue component) */
  haloHue: number;
  /** Halo animation: breathing speed in seconds (0 = no breathing) */
  haloBreathDuration: number;

  /** Stage ambient light opacity (the outermost radial glow) */
  stageAmbient: number;
  /** Stage vignette strength (0–1, higher = darker edges) */
  stageVignette: number;
  /** Floor reflection opacity */
  floorReflection: number;

  /** Vertical beam opacity (the subtle light column behind the avatar) */
  beamOpacity: number;
  /** Light sweep animation speed in seconds */
  sweepDuration: number;

  /** Transition duration for this state change (seconds) */
  transitionDuration: number;
}

/**
 * State-specific visual configurations.
 *
 * idle      → silent, alive, calm presence
 * listening → attentive, slightly brighter, focused
 * thinking  → concentrated, contained, cooler
 * speaking  → responsive, energised, warmer
 */
export const PRESENCE_STATES: Record<MirrorPresenceState, PresenceVisuals> = {
  idle: {
    avatarFilter: "brightness(1) contrast(1.04) saturate(1)",
    avatarOpacity: 0.94,
    avatarScale: 1.32,
    haloIntensity: 0.06,
    haloSize: 520,
    haloHue: 187,
    haloBreathDuration: 5,
    stageAmbient: 0.04,
    stageVignette: 0.85,
    floorReflection: 0.06,
    beamOpacity: 0.4,
    sweepDuration: 8,
    transitionDuration: 1.4,
  },

  listening: {
    avatarFilter: "brightness(1.08) contrast(1.06) saturate(1.1)",
    avatarOpacity: 0.97,
    avatarScale: 1.33,
    haloIntensity: 0.12,
    haloSize: 560,
    haloHue: 187,
    haloBreathDuration: 2.5,
    stageAmbient: 0.07,
    stageVignette: 0.78,
    floorReflection: 0.1,
    beamOpacity: 0.7,
    sweepDuration: 4,
    transitionDuration: 0.9,
  },

  thinking: {
    avatarFilter: "brightness(0.92) contrast(1.06) saturate(0.88)",
    avatarOpacity: 0.92,
    avatarScale: 1.31,
    haloIntensity: 0.08,
    haloSize: 440,
    haloHue: 210,
    haloBreathDuration: 3.5,
    stageAmbient: 0.03,
    stageVignette: 0.9,
    floorReflection: 0.04,
    beamOpacity: 0.5,
    sweepDuration: 12,
    transitionDuration: 1.0,
  },

  speaking: {
    avatarFilter: "brightness(1.06) contrast(1.04) saturate(1.06)",
    avatarOpacity: 0.96,
    avatarScale: 1.33,
    haloIntensity: 0.14,
    haloSize: 580,
    haloHue: 187,
    haloBreathDuration: 2,
    stageAmbient: 0.08,
    stageVignette: 0.72,
    floorReflection: 0.12,
    beamOpacity: 0.65,
    sweepDuration: 5,
    transitionDuration: 0.8,
  },
};

/** Mirror stage background color — deep navy-black */
export const MIRROR_STAGE_BG = "#06111C";
